import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom"
import { FaHeart, FaUsers, FaStar, FaInfinity, FaRing, FaShieldAlt } from "react-icons/fa";
import { useLanguage } from '../../context/LanguageContext';
import PageHeader from './PageHeader'; 

const AboutUsSection = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const t = {
    English: {
      title: "About Kalyana Matrimony",
      subtitle: "Where Hearts Find Their Perfect Match",
      description: "Kalyana Matrimony is a trusted matrimonial platform dedicated to helping individuals find their life partners with ease, privacy, and authenticity. Rooted in tradition yet powered by modern technology, we bring together compatible souls for lifelong happiness.",
      missionTitle: "Our Mission",
      mission: "To create meaningful connections that lead to happy, everlasting marriages by combining cultural values with smart matchmaking.",
      visionTitle: "Our Vision",
      vision: "A world where every heart finds its true match, building stronger families and happier lives.",
      whyUs: [
        { icon: <FaHeart />, title: "Genuine Profiles", desc: "100% verified members for complete trust" },
        { icon: <FaShieldAlt />, title: "Complete Privacy", desc: "Your data is safe and confidential" },
        { icon: <FaUsers />, title: "Smart Matching", desc: "Advanced algorithms for perfect compatibility" },
        { icon: <FaStar />, title: "Success Stories", desc: "Thousands of happy couples united" }
      ],
      stats: [
        { number: "1,00,000+", label: "Registered Members" },
        { number: "10,000+", label: "Successful Matches" },
        { number: "98%", label: "Satisfaction Rate" },
        { number: "24/7", label: "Support Available" }
      ],
      cta: "Join Us Today and Find Your Soulmate",
      pageHeaderTitle: "About Us"
    },
    Tamil: {
      title: "Kalyana Matrimony பற்றி",
      subtitle: "இதயங்கள் தங்கள் சரியான இணையை கண்டுபிடிக்கும் இடம்",
      description: "Kalyana Matrimony என்பது தனிநபர்கள் தங்கள் வாழ்க்கைத் துணையை எளிதாகவும், தனியுரிமையுடனும், உண்மையாகவும் கண்டறிய உதவும் நம்பகமான திருமண இணையதளம். பாரம்பரியத்தில் வேரூன்றிய அதே வேளையில் நவீன தொழில்நுட்பத்தால் இயக்கப்படும் நாங்கள், வாழ்நாள் முழுவதும் மகிழ்ச்சியைத் தரும் இணையான ஆன்மாக்களை ஒன்றிணைக்கிறோம்.",
      missionTitle: "எங்கள் நோக்கம்",
      mission: "கலாச்சார மதிப்புகளை புத்திசாலித்தனமான இணைப்புடன் இணைத்து, மகிழ்ச்சியான, நிலையான திருமணங்களுக்கு வழிவகுக்கும் அர்த்தமுள்ள தொடர்புகளை உருவாக்குதல்.",
      visionTitle: "எங்கள் பார்வை",
      vision: "ஒவ்வொரு இதயமும் அதன் உண்மையான இணையைக் கண்டறியும் உலகம், வலுவான குடும்பங்களையும் மகிழ்ச்சியான வாழ்க்கையையும் உருவாக்குதல்.",
      whyUs: [
        { icon: <FaHeart />, title: "உண்மையான சுயவிவரங்கள்", desc: "100% சரிபார்க்கப்பட்ட உறுப்பினர்கள் முழு நம்பிக்கைக்கு" },
        { icon: <FaShieldAlt />, title: "முழு தனியுரிமை", desc: "உங்கள் தரவு பாதுகாப்பானது மற்றும் ரகசியமானது" },
        { icon: <FaUsers />, title: "புத்திசாலி இணைத்தல்", desc: "சரியான இணக்கத்திற்கான மேம்பட்ட அல்காரிதங்கள்" },
        { icon: <FaStar />, title: "வெற்றிக் கதைகள்", desc: "ஆயிரக்கணக்கான மகிழ்ச்சியான தம்பதிகள் இணைக்கப்பட்டனர்" }
      ],
      stats: [
        { number: "1,00,000+", label: "பதிவு செய்த உறுப்பினர்கள்" },
        { number: "10,000+", label: "வெற்றிகரமான இணைப்புகள்" },
        { number: "98%", label: "திருப்தி விகிதம்" },
        { number: "24/7", label: "ஆதரவு கிடைக்கும்" }
      ],
      cta: "இன்று எங்களுடன் சேர்ந்து உங்கள் ஆன்ம துணையைக் கண்டறியுங்கள்",
      pageHeaderTitle: "எங்களை பற்றி"
    }
  };

  const txt = t[language];

  return (
    <>
      {/* Reusable Page Header */}
      <PageHeader 
        showBackButton={true} 
        backTo="/" 
        title={txt.pageHeaderTitle} // Optional: pass title if your PageHeader supports it
      />

      <section className="relative py-16 md:py-20 lg:py-24 px-4 md:px-8 bg-gradient-to-br from-rose-50 via-white to-pink-50 overflow-hidden">
        {/* Subtle Background Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-80 h-80 bg-rose-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-1000"></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          {/* Hero Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12 md:mb-16"
          >
            <motion.div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-rose-100 text-rose-700 font-medium text-base md:text-lg uppercase tracking-wider mb-6">
              <FaHeart className="animate-pulse text-xl" />
              {language === 'English' ? 'Love Begins Here' : 'காதல் இங்கே தொடங்குகிறது'}
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-4 leading-tight">
              {txt.title}
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl font-light text-rose-600 mb-6">
              {txt.subtitle}
            </p>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {txt.description}
            </p>
          </motion.div>

          {/* Mission & Vision */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-rose-100"
            >
              <h3 className="text-2xl md:text-3xl font-bold text-rose-600 mb-4 flex items-center gap-3">
                <FaInfinity className="text-3xl" /> {txt.missionTitle}
              </h3>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed">{txt.mission}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-rose-100"
            >
              <h3 className="text-2xl md:text-3xl font-bold text-pink-600 mb-4 flex items-center gap-3">
                <FaRing className="text-3xl" /> {txt.visionTitle}
              </h3>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed">{txt.vision}</p>
            </motion.div>
          </div>

          {/* Why Choose Us */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-10">
              {language === 'English' ? 'Why Choose Kalyana Matrimony?' : 'ஏன் Kalyana Matrimony-ஐ தேர்ந்தெடுக்க வேண்டும்?'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {txt.whyUs.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                  className="text-center bg-white rounded-2xl p-6 shadow-md border border-rose-100"
                >
                  <div className="inline-flex p-5 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 text-white text-3xl mb-4">
                    {item.icon}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-gray-600 text-sm md:text-base">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center mb-12">
            {txt.stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <p className="text-3xl md:text-4xl font-extrabold text-rose-600">{stat.number}</p>
                <p className="text-base text-gray-600 mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center">
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate("/register")}
        className="px-10 py-4 bg-gradient-to-r from-rose-500 to-pink-500 text-white text-lg font-bold rounded-xl shadow-xl flex items-center gap-3 mx-auto group"
      >
        {txt.cta}
        <FaHeart className="group-hover:scale-125 transition-transform" />
      </motion.button>
    </div>
        </div>
      </section>
    </>
  );
};

export default AboutUsSection;